import React, { memo, useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { Accordion } from "react-bootstrap";
import { Card, CardBody } from "reactstrap";
import {
    AI_RISK_LEVEL_COLOR,
    AI_RISK_LEVEL_ORDER,
    AI_RISK_LEVEL_TITLE,
} from "../ai-risk-chart-constants";
import "./style.scss";

const AiModelsByRiskLevels = memo(({ t, modelsByRisk = {}, isFetching }) => {
    const sections = useMemo(
        () =>
            AI_RISK_LEVEL_ORDER.map((level) => ({
                level,
                title: AI_RISK_LEVEL_TITLE[level],
                color: AI_RISK_LEVEL_COLOR[level] || "#90A4AE",
                models: Array.isArray(modelsByRisk?.[level]) ? modelsByRisk[level] : [],
            })),
        [modelsByRisk]
    );

    const defaultActiveKey = useMemo(() => {
        const firstWithModels = sections.find((section) => section.models.length > 0);
        return firstWithModels?.level ?? sections[0]?.level ?? "prohibited";
    }, [sections]);

    const [activeKey, setActiveKey] = useState(defaultActiveKey);

    useEffect(() => {
        setActiveKey(defaultActiveKey);
    }, [defaultActiveKey]);

    if (isFetching) {
        return (
            <div className="dt-field dt-skeleton mb-3" style={{ width: "100%", height: "460px" }} />
        );
    }

    return (
        <Card className="mb-3 card_box_Shadow flex-grow-1 ai-models-by-risk-levels" style={{ height: "460px" }}>
            <CardBody
                className="d-flex flex-column h-100"
                style={{ overflow: "hidden" }}
            >
                <h6 className="mb-3 fw-bold flex-shrink-0">{t("AI Models By Risk Levels")}</h6>

                <div className="ai-models-by-risk-levels-accordion-wrap flex-grow-1 overflow-auto">
                    <Accordion
                        activeKey={activeKey}
                        onSelect={(key) => setActiveKey(key)}
                        className="ai-models-by-risk-levels-accordion"
                    >
                    {sections.map((section) => (
                        <Accordion.Item
                            key={section.level}
                            eventKey={section.level}
                            className={activeKey === section.level ? "active" : ""}
                        >
                            <Accordion.Header
                                style={{ backgroundColor: section.color }}
                                className="ai-models-by-risk-levels-accordion__header"
                            >
                                <span className="fw-semibold text-white">{t(section.title)}</span>
                            </Accordion.Header>

                            <Accordion.Body>
                                {section.models.length > 0 ? (
                                    <ul className="mb-0 ps-3">
                                        {section.models.map((modelName, index) => (
                                            <li
                                                key={`${section.level}-${index}-${modelName}`}
                                                className="text-muted small mb-1"
                                            >
                                                {modelName}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-muted small mb-0">
                                        {t("There is no data")}
                                    </p>
                                )}
                            </Accordion.Body>
                        </Accordion.Item>
                    ))}
                    </Accordion>
                </div>
            </CardBody>
        </Card>
    );
});

export default withNamespaces()(AiModelsByRiskLevels);
